import { type DQLHTTPResponse, isDQLHTTPResponse } from '@portal/dql-editor'
import axios from 'axios'

/** Module used for providing helper functions to the Big Peer HTTP/RPC API */

// Hydra client ID
export const CLIENT_ID = 'AAAAAAAAAAAAAAAAAAAAew=='

type RpcApiVersion = 'v1' | 'v2' | 'v3' | 'v4'

/** Generates the URL to access the RPC API */
const rpcApiUrl = (
  appDomain: string,
  appId: string,
  version: RpcApiVersion,
  store = true,
) => {
  const appSegment =
    process.env.NODE_ENV === 'development' &&
    !appDomain.includes('dittolive.app/')
      ? `${appDomain}/${appId}`
      : appDomain
  return `https://${appSegment}/api/${version}${store ? '/store' : ''}`
}

/** Generates the URL for the Find method of the RPC API */
export const rpcApiFindUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4') + '/find'

/** Generates the URL for the FindById method of the RPC API */
export const rpcApiFindByIdUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4') + '/findbyid'

/** Generates the URL for the Write method of the RPC API */
export const rpcApiWriteUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4') + '/write'

/** Generates the URL for the Count method of the RPC API */
export const rpcApiCountUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v3') + '/count'

export const rpcApiExecuteDqlQueryUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4') + '/execute'

export const rpcApiAttachmentUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4', false) + '/attachments'

// Function used for retrieving a JWT access token from the RPC client, given a local token.
// This will rely on the portal token validation webhook to authenticate the user
export const rpcLogin = async (
  appDomain: string,
  appId: string,
  provider: string,
  userToken: string,
): Promise<{
  accessToken: string
  tokenExpiration: string
}> => {
  const response = await axios.post(
    `https://${appDomain}/_ditto/auth/http_login`,
    `appId=${appId}&token=${userToken}&provider=${provider}`,
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  )

  return {
    accessToken: response.data.httpToken,
    tokenExpiration: response.data.tokenExpiry,
  }
}

const rpcDefaultHeaders = (accessToken: string) => ({
  'X-DITTO-CLIENT-ID': CLIENT_ID,
  'Content-Type': 'application/json',
  Authorization: `Bearer ${accessToken}`,
})

// Function used to check whether a collection is empty using an app's RPC endpoint.
export const isEmptyCollection = async (
  collection: string,
  query: string,
  appId: string,
  appDomain: string,
  accessToken: string,
) => {
  const response = await axios.post(
    rpcApiFindUrl(appDomain, appId),
    {
      collection,
      query,
      limit: 1,
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return !(response.data?.documents || []).length
}

export const getCollectionDocumentCount = async (
  collection: string,
  query: string,
  appId: string,
  appDomain: string,
  accessToken: string,
): Promise<number> => {
  const respones = await axios.post(
    rpcApiCountUrl(appDomain, appId),
    {
      collection,
      query: query || 'true',
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return respones.data?.count || 0
}

type LegacyFindOptions = {
  formatAttachment?: boolean
}

type LegacySort = {
  property: string
  direction: 'asc' | 'desc'
}

export type LegacyFindParams = {
  query: string
  collection: string
  args?: Record<string, unknown>
  options?: LegacyFindOptions
  sort?: LegacySort[]
  limit?: number
}

export type LegacyHTTPDocument = {
  id: string
  fields: Record<string, unknown>
}

export type LegacyFindResponse = {
  documents: LegacyHTTPDocument[]
  txnId: string
}

export type LegacyFindByIdResponse = {
  txnId: string
} & LegacyHTTPDocument

export function isLegacyFindResponse<T>(
  response: T | LegacyFindResponse,
): response is LegacyFindResponse {
  return !!response && typeof response === 'object' && 'documents' in response
}

export function isLegacyFindByIdResponse<T>(
  response: T | LegacyFindByIdResponse,
): response is LegacyFindByIdResponse {
  // Note: having a valid base without fields is indicative of a non-existent document
  const isValidBase =
    !!response && typeof response === 'object' && 'txnId' in response
  if (isValidBase && 'fields' in response) {
    return (
      'id' in response &&
      typeof response.id === 'string' &&
      typeof response.fields === 'object'
    )
  }
  return isValidBase
}

export const executeLegacyFindQuery = async (
  { query, collection, args, options, sort, limit }: LegacyFindParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<LegacyFindResponse> => {
  const { data, status } = await axios.post(
    rpcApiFindUrl(appDomain, appId),
    {
      query,
      collection,
      args,
      sort,
      limit,
      ...options,
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  if (!isLegacyFindResponse(data)) {
    throw new Error('Invalid response received from RPC client', {
      cause: { status, data },
    })
  }

  return data
}

export type LegacyFindByIdParams = {
  id: string
} & Pick<LegacyFindParams, 'collection' | 'options'>

export const executeLegacyFindByIdQuery = async (
  { id, collection, options }: LegacyFindByIdParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<LegacyFindByIdResponse | null> => {
  const { data, status } = await axios.post(
    rpcApiFindByIdUrl(appDomain, appId),
    {
      id,
      collection,
      ...options,
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  if (!isLegacyFindByIdResponse(data)) {
    throw new Error('Invalid response received from RPC client', {
      cause: { status, data },
    })
  }

  if (!data.id || !data.fields) {
    return null
  }

  return data
}

export type LegacyUpdateParams = {
  query: string
  collection: string
  updates: Array<{
    method?: 'set'
    path: string
    value: unknown
  }>
}

export const executeLegacyUpdate = async (
  { query, collection, updates }: LegacyUpdateParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<unknown> => {
  const response = await axios.post(
    rpcApiWriteUrl(appDomain, appId),
    {
      commands: [
        {
          method: 'update',
          collection,
          query,
          commands: updates.map(({ method = 'set', ...rest }) => ({
            method,
            ...rest,
          })),
        },
      ],
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return response.data
}

export type LegacyUpsertParams = {
  collection: string
  id: string
  value: Record<string, unknown>
  valueTypeOverrides?: Record<string, string>
}

export const executeLegacyUpsert = async (
  { collection, id, value, valueTypeOverrides }: LegacyUpsertParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<unknown> => {
  const response = await axios.post(
    rpcApiWriteUrl(appDomain, appId),
    {
      commands: [
        {
          method: 'upsert',
          collection,
          id,
          value,
          valueTypeOverrides,
        },
      ],
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return response.data
}

export type LegacyRemoveParams = Omit<LegacyUpdateParams, 'updates'>

export const executeLegacyRemove = async (
  { query, collection }: LegacyRemoveParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<unknown> => {
  const response = await axios.post(
    rpcApiWriteUrl(appDomain, appId),
    {
      commands: [
        {
          method: 'remove',
          collection,
          query,
        },
      ],
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return response.data
}

export const downloadAttachment = async (
  id: string,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<Uint8Array> => {
  const { data } = await axios.get(
    rpcApiAttachmentUrl(appDomain, appId) + `/${id}`,
    {
      headers: rpcDefaultHeaders(accessToken),
      responseType: 'arraybuffer',
    },
  )

  return Buffer.from(data, 'binary')
}

export const executeDqlQuery = async (
  query: string,
  appDomain: string,
  appId: string,
  accessToken: string,
  args?: Record<string, unknown>,
): Promise<DQLHTTPResponse> => {
  try {
    const response = await axios.post(
      rpcApiExecuteDqlQueryUrl(appDomain, appId),
      {
        statement: query,
        args,
      },
      {
        headers: rpcDefaultHeaders(accessToken),
        // We don't want to throw ANY errors here, as we are generally always expecting a valid response
        // object. We will need to validate that it matches the expected format.
        validateStatus: () => true,
      },
    )

    // NOTE: We can also add logic to verify the Query-Subtype header is present to determine validity
    // of the response object. Since that doesn't validate the _actual_ structure of what we received, I
    // opted to go this route instead.
    const isValid = isDQLHTTPResponse(response.data)
    if (!isValid) {
      const fallback =
        response.status > 200
          ? `Request could not be completed: ${response.statusText}`
          : 'An invalid response was received. Please try again'
      throw new Error(response.data?.error?.description || fallback)
    }

    return response.data as DQLHTTPResponse
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      const { message } = error.response.data as { message?: string }
      throw new Error(message || error.message)
    } else {
      throw error
    }
  }
}
